/**
 * Client-side reCAPTCHA v3 token helper.
 *
 * The site key is injected by the root layout as `window.__RECAPTCHA_SITE_KEY__`
 * (a ~50-byte inline script) only when the tenant has reCAPTCHA enabled. The
 * ~100 KB `api.js` is NOT loaded by the layout — it's fetched on demand here,
 * the first time a form actually needs a token, so pages with no form never
 * pay for it. When the key is absent, getRecaptchaToken resolves to '' — forms
 * still submit and the backend fails open. Backend reads it as `captcha_response`.
 */

type Grecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    __RECAPTCHA_SITE_KEY__?: string;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-recaptcha]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('recaptcha')));
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.dataset.recaptcha = 'true';
    s.addEventListener('load', () => resolve());
    s.addEventListener('error', () => reject(new Error('recaptcha')));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export async function getRecaptchaToken(action: string): Promise<string> {
  if (typeof window === 'undefined') return '';
  const siteKey = window.__RECAPTCHA_SITE_KEY__;
  if (!siteKey) return ''; // not configured → fail open server-side

  try {
    await loadRecaptchaScript(siteKey);
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return '';
    await new Promise<void>((resolve) => grecaptcha.ready(resolve));
    return await grecaptcha.execute(siteKey, { action });
  } catch {
    return '';
  }
}
